import React, { useState } from 'react'
import {
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  message,
  Grid
} from 'antd'
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ProjectOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useProjects } from '../../hooks/useProjects'
import api from '../../utils/api'

const { Title, Text } = Typography
const { Option } = Select
const { useBreakpoint } = Grid
const { TextArea } = Input

const CreateProjectPage = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const { createProject, loading } = useProjects({ autoFetch: false })
  const [submitting, setSubmitting] = useState(false)
  const [managers, setManagers] = useState([])
  const [clients, setClients] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(true)

  // Mock data for managers and clients - ใน production จะเรียก API จริง
  React.useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true)
        // Mock data สำหรับทดสอบ - ใน production จะเรียก API จริง
        setManagers([
          { value: 1, label: 'สมชาย ใจดี (Project Manager)' },
          { value: 2, label: 'สมหญิง มั่นคง (Senior Developer)' },
          { value: 3, label: 'วิชาญ ทันสมัย (UI/UX Designer)' },
          { value: 4, label: 'ปรารถนา เชี่ยวชาญ (DevOps Engineer)' }
        ])
        
        setClients([
          { value: 1, label: 'บริษัท ABC จำกัด' },
          { value: 2, label: 'บริษัท XYZ จำกัด' },
          { value: 3, label: 'ร้านค้าออนไลน์ DEF' },
          { value: 4, label: 'องค์กร GHI' }
        ])
      } catch (error) {
        message.error('ไม่สามารถโหลดข้อมูลได้')
      } finally {
        setLoadingOptions(false)
      }
    }

    fetchOptions()
  }, [])

  // ตรวจสอบ permission
  React.useEffect(() => {
    const hasPermission = localStorage.getItem('permissions')?.includes('projects.create')
    if (!hasPermission) {
      message.error('คุณไม่มีสิทธิ์ในการสร้างโครงการ')
      navigate('/projects')
    }
  }, [navigate])

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true)

      // แปลงวันที่ให้เป็น format ที่ API ต้องการ
      const projectData = {
        ...values,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        expected_completion_date: values.expected_completion_date 
          ? values.expected_completion_date.format('YYYY-MM-DD') 
          : null
      }

      console.log('Creating project with data:', projectData)

      await createProject(projectData)
      
      message.success('สร้างโครงการใหม่สำเร็จ')
      navigate('/projects')
    } catch (error) {
      console.error('Error creating project:', error)
      if (error.errorFields) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('ไม่สามารถสร้างโครงการได้')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    navigate('/projects')
  }

  // ตัวเลือกสถานะโครงการ
  const statusOptions = [
    { value: 'planning', label: 'วางแผน' },
    { value: 'active', label: 'กำลังดำเนินการ' },
    { value: 'on_hold', label: 'พักชั่วคราว' },
    { value: 'completed', label: 'เสร็จสิ้น' },
    { value: 'cancelled', label: 'ยกเลิก' }
  ]

  // ตัวเลือกหมวดหมู่โครงการ
  const categoryOptions = [
    { value: 'web_development', label: 'พัฒนาเว็บไซต์' },
    { value: 'mobile_development', label: 'พัฒนาแอปมือถือ' },
    { value: 'data_analytics', label: 'วิเคราะห์ข้อมูล' },
    { value: 'consulting', label: 'ที่ปรึกษา' },
    { value: 'maintenance', label: 'บำรุงรักษา' },
    { value: 'training', label: 'อบรม' },
    { value: 'research', label: 'วิจัยและพัฒนา' },
    { value: 'other', label: 'อื่นๆ' }
  ]

  // ตัวเลือกระดับความสำคัญ
  const priorityOptions = [
    { value: 'low', label: 'ต่ำ' },
    { value: 'medium', label: 'ปานกลาง' },
    { value: 'high', label: 'สูง' },
    { value: 'urgent', label: 'เร่งด่วน' }
  ]

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/projects')}
                size={isMobile ? 'small' : 'middle'}
              >
                กลับ
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                <ProjectOutlined /> สร้างโครงการใหม่
              </Title>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Form */}
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'planning',
            priority: 'medium',
            category: 'web_development',
            budget: 0
          }}
          disabled={submitting || loading}
        >
          {/* ข้อมูลพื้นฐานโครงการ */}
          <Divider orientation="left">
            <Space>
              <ProjectOutlined />
              <Text strong>ข้อมูลพื้นฐานโครงการ</Text>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="project_name"
                label="ชื่อโครงการ"
                rules={[
                  { required: true, message: 'กรุณาใส่ชื่อโครงการ' },
                  { min: 3, message: 'ชื่อโครงการต้องมีอย่างน้อย 3 ตัวอักษร' }
                ]}
              >
                <Input 
                  placeholder="เช่น โครงการพัฒนาเว็บไซต์บริษัท ABC"
                  prefix={<ProjectOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="project_code"
                label="รหัสโครงการ"
                rules={[
                  { required: true, message: 'กรุณาใส่รหัสโครงการ' },
                  { 
                    pattern: /^[A-Z0-9_-]+$/, 
                    message: 'รหัสโครงการต้องเป็นตัวพิมพ์ใหญ่ ตัวเลข เครื่องหมายขีดกลาง และเครื่องหมายขีดล่างเท่านั้น'
                  }
                ]}
              >
                <Input 
                  placeholder="เช่น PRJ-2024-001"
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    form.setFieldValue('project_code', e.target.value.toUpperCase())
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="คำอธิบายโครงการ"
          >
            <TextArea
              rows={4}
              placeholder="อธิบายรายละเอียดของโครงการ เป้าหมาย และขอบเขตงาน..."
              prefix={<FileTextOutlined />}
            />
          </Form.Item>

          {/* หมวดหมู่และความสำคัญ */}
          <Row gutter={16}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="category"
                label="หมวดหมู่โครงการ"
                rules={[{ required: true, message: 'กรุณาเลือกหมวดหมู่โครงการ' }]}
              >
                <Select placeholder="เลือกหมวดหมู่โครงการ">
                  {categoryOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="priority"
                label="ระดับความสำคัญ"
                rules={[{ required: true, message: 'กรุณาเลือกระดับความสำคัญ' }]}
              >
                <Select placeholder="เลือกระดับความสำคัญ">
                  {priorityOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* สถานะและงบประมาณ */}
          <Row gutter={16}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="status"
                label="สถานะโครงการ"
                rules={[{ required: true, message: 'กรุณาเลือกสถานะโครงการ' }]}
              >
                <Select placeholder="เลือกสถานะโครงการ">
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="budget"
                label="งบประมาณ (บาท)"
                rules={[
                  { required: true, message: 'กรุณาใส่งบประมาณ' },
                  { type: 'number', min: 0, message: 'งบประมาณต้องเป็นตัวเลขบวก' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                  addonAfter="฿"
                  prefix={<DollarOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* กำหนดการ */}
          <Divider orientation="left">
            <Space>
              <CalendarOutlined />
              <Text strong>กำหนดการ</Text>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="start_date"
                label="วันที่เริ่มโครงการ"
                rules={[{ required: true, message: 'กรุณาเลือกวันที่เริ่มโครงการ' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="เลือกวันที่เริ่มโครงการ"
                  format="DD/MM/YYYY"
                  disabledDate={(current) => current && current < new Date()}
                />
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="expected_completion_date"
                label="วันที่คาดว่าจะเสร็จ"
                rules={[
                  { required: true, message: 'กรุณาเลือกวันที่คาดว่าจะเสร็จ' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || !getFieldValue('start_date')) {
                        return Promise.resolve()
                      }
                      if (value <= getFieldValue('start_date')) {
                        return Promise.reject(new Error('วันที่คาดว่าจะเสร็จต้องมากกว่าวันที่เริ่มโครงการ'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="เลือกวันที่คาดว่าจะเสร็จ"
                  format="DD/MM/YYYY"
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue('start_date')
                    if (!startDate) {
                      return current && current < new Date()
                    }
                    return current && current <= startDate.startOf('day')
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ผู้จัดการและลูกค้า */}
          <Divider orientation="left">
            <Space>
              <UserOutlined />
              <Text strong>ผู้จัดการและลูกค้า</Text>
            </Space>
          </Divider>

          <Row gutter={16}>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="manager_id"
                label="ผู้จัดการโครงการ"
                rules={[{ required: true, message: 'กรุณาเลือกผู้จัดการโครงการ' }]}
              >
                <Select 
                  placeholder="เลือกผู้จัดการโครงการ"
                  loading={loadingOptions}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {managers.map(manager => (
                    <Option key={manager.value} value={manager.value}>
                      {manager.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={isMobile ? 24 : 12}>
              <Form.Item
                name="client_id"
                label="ลูกค้า"
                rules={[{ required: true, message: 'กรุณาเลือกลูกค้า' }]}
              >
                <Select 
                  placeholder="เลือกลูกค้า"
                  loading={loadingOptions}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {clients.map(client => (
                    <Option key={client.value} value={client.value}>
                      {client.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* ปุ่มควบคุม */}
          <Divider />
          
          <Row justify="end" gutter={16}>
            <Col>
              <Button
                size={isMobile ? 'large' : 'middle'}
                onClick={handleCancel}
                disabled={submitting}
              >
                ยกเลิก
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                size={isMobile ? 'large' : 'middle'}
                icon={<SaveOutlined />}
                loading={submitting}
              >
                สร้างโครงการ
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  )
}

export default CreateProjectPage